/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaTemplateSystemsPane.tsx
 * FILE_ID: QA-TEMPLATES-SYSTEMS-PANE-001
 * PURPOSE: Middle column of published systems for the selected location type.
 */
import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { QaTemplatePicker } from './useQaTemplatePicker'

function systemSubtitle(template: { section_count?: number; item_count?: number }): string {
  const checks = template.section_count ?? 0
  if (checks === 0) return 'template empty'
  const checkLabel = `${checks} checklist${checks === 1 ? '' : 's'}`
  if (template.item_count == null) return checkLabel
  const steps = template.item_count
  return `${checkLabel} · ${steps} step${steps === 1 ? '' : 's'}`
}

export function QaTemplateSystemsPane({
  picker,
  collapsible = false,
}: {
  picker: QaTemplatePicker
  collapsible?: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const shown = !collapsible || expanded
  const label = picker.groups.find((group) => group.location_type === picker.locationType)?.label ?? 'Systems'
  const heading = picker.locationType ? `${label} systems` : 'Systems'
  return (
    <div className="flex min-h-0 flex-col">
      {collapsible ? (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          className="mb-1 flex min-h-[44px] w-full items-center gap-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
          {heading}
        </button>
      ) : (
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {heading}
        </p>
      )}
      {shown ? <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {picker.templates.map((template) => {
          const inBasket = picker.isInBasket(template.id)
          return (
            <div key={template.id} className="flex items-start gap-2">
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 rounded border-border"
                aria-label={`Select ${template.name}`}
                checked={inBasket}
                onClick={(event) => event.stopPropagation()}
                onChange={() => {
                  if (inBasket) picker.removeFromBasket(template.id)
                  else picker.pickTemplate(template.id)
                }}
              />
              <button
                type="button"
                onClick={() => picker.pickTemplate(template.id)}
                className={`flex min-h-[44px] w-full flex-1 flex-col items-start rounded-md border border-border px-2 py-2 text-left ${
                  picker.templateId === template.id ? 'bg-muted' : 'hover:bg-muted/50'
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="min-w-0 truncate text-sm font-medium">{template.name}</span>
                  {template.job != null ? (
                    <span className="shrink-0 rounded-full bg-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
                      This job
                    </span>
                  ) : null}
                </span>
                <span className="mt-1 text-[11px] text-muted-foreground">
                  {systemSubtitle(template)}
                </span>
              </button>
            </div>
          )
        })}
      </div> : null}
    </div>
  )
}
